import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import { updateJob } from '../services/api';

// A record saved under a retired option — the old bare 'Approved' job status, 'Imported',
// legacy 'In Progress - Field Work' — must still appear in its own dropdown. Without this
// the select renders blank and the next save silently rewrites the field.
const withCurrentOption = (options, current) => (
  current && !options.some(o => (typeof o === 'string' ? o : o.value) === current)
    ? [...options, current]
    : options
);

// Reusable focused editor for a single section's fields.
// fields: [{ key, label, type: 'text'|'textarea'|'date'|'number'|'select', options?: string[] }]
// job: current job object (for initial values). onSaved(): called after a successful save.
export default function JobFieldEditModal({ jobId, title, fields, job, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = {};
    fields.forEach(fd => { f[fd.key] = job[fd.key] != null ? job[fd.key] : ''; });
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setVal = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await updateJob(jobId, form);
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      console.error('Error saving section:', e);
      setError(e?.response?.data?.error || 'Could not save changes');
      setSaving(false);
    }
  };

  return (
    <div className="qv-modal-overlay" onClick={onClose}>
      <div className="qv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qv-modal-head">
          <h3>Edit {title}</h3>
          <button className="btn-ghost" onClick={onClose}><FiX /> Close</button>
        </div>
        <div className="qv-modal-body">
          {fields.map(fd => (
            <div key={fd.key} className={`qv-field${fd.type === 'textarea' ? ' qv-field-wide' : ''}`}>
              <label>{fd.label}</label>
              {fd.type === 'select' ? (
                <select value={form[fd.key] || ''} onChange={(e) => setVal(fd.key, e.target.value)}>
                  {/* Options are plain strings ("Boundary") or {value,label} pairs, for
                      fields whose stored code differs from what a person should read
                      — e.g. monument_recovery stores 'none' but means
                      "Almost none - rebuilt from deeds". */}
                  {withCurrentOption(fd.options, form[fd.key]).map((o) => {
                    const value = typeof o === 'string' ? o : o.value;
                    const label = typeof o === 'string' ? o : o.label;
                    return <option key={value} value={value}>{label || '— None —'}</option>;
                  })}
                </select>
              ) : fd.type === 'textarea' ? (
                <textarea rows={3} value={form[fd.key] || ''} onChange={(e) => setVal(fd.key, e.target.value)} />
              ) : (
                <input
                  type={fd.type === 'date' ? 'date' : (fd.type === 'number' ? 'number' : 'text')}
                  value={form[fd.key] || ''}
                  onChange={(e) => setVal(fd.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', padding: '0 1.5rem' }}>{error}</div>}
        <div className="qv-modal-foot">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
